import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Flex, Input, Select, Table, Thead, Tbody,
    Tr, Th, Td, Badge, Button, Stack, Avatar, AvatarGroup, Tooltip,
    Text, Icon, InputGroup, InputLeftElement, HStack, Switch, FormControl, ButtonGroup,
    FormLabel, IconButton, Spinner, Modal, ModalOverlay, ModalContent,
    ModalHeader, ModalFooter, ModalBody, ModalCloseButton, VStack, Divider, Tag, useToast
} from '@chakra-ui/react';
import { FaSearch, FaExternalLinkAlt, FaPencilAlt, FaEye, FaDownload, FaTimesCircle, FaBell } from 'react-icons/fa';
import TicketNotifyDialog from '../TicketNotifyDialog';
import { WikiContentViewer } from '../WikiEditor';
import { formatDistanceToNowStrict } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getTicket, updateTicket, downloadTicketAttachment, getUsers } from '../../services/api';
import { semBloqueados } from '../../hooks/useUsuarios';

const formatDistance = (dateString) => {
    if (!dateString) return '-';
    return formatDistanceToNowStrict(new Date(dateString), { addSuffix: true, locale: ptBR });
}

const JANELAS_ATIVIDADE = [7, 14, 30, 60];

const statusColors = { aberto: 'blue', em_andamento: 'orange', bloqueado: 'purple', fechado: 'green' };
const statusLabels = { aberto: 'Aberto', em_andamento: 'Em Andamento', bloqueado: 'Bloqueado', fechado: 'Concluido' };
const priorityColors = { baixa: 'green', media: 'yellow', alta: 'orange', urgente: 'red' };

const TicketListPage = ({ tickets, paginationData, loading, filters, setFilters, onUpdateSuccess, onClearFilters, hideTable, viewToggle }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [modalDetails, setModalDetails] = useState(null);
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [modalStatus, setModalStatus] = useState('');
    const toast = useToast();
    const [downloadingId, setDownloadingId] = useState(null);
    const [isNotifyOpen, setIsNotifyOpen] = useState(false);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        const userString = window.localStorage.getItem("user");
        if (userString) {
            try {
                setCurrentUser(JSON.parse(userString));
            } catch (error) {
                console.error("Erro ao parsear dados do usuário do localStorage:", error);
            }
        }

        const fetchUsers = async () => {
            try {
                const userListResponse = await getUsers();
                setUsers(semBloqueados(userListResponse.data || []));
            } catch (error) {
                console.error("Não foi possível carregar a lista de usuários para os filtros.");
            }
        };

        fetchUsers();
    }, []);

    const handleDownloadAttachment = async (fileId, fileName) => {
        setDownloadingId(fileId);
        try {
            const response = await downloadTicketAttachment(fileId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast({
                title: "Erro no Download",
                description: "Não foi possível baixar o anexo. Tente novamente.",
                status: "error",
                isClosable: true,
            });
        } finally {
            setDownloadingId(null);
        }
    };

    const getImgUser = (usuario_id) => {
        var img = "user.png";
        if (usuario_id == 3) {
            img = "/gabriel_logo.webp";
        } else if (usuario_id == 5) {
            img = "/tilio_logo.webp";
        } else if (usuario_id == 6) {
            img = "/riccioppo_logo.webp";
        } else if (usuario_id == 7) {
            img = "/user.png";
        } else if (usuario_id == 8) {
            img = "/gabriel_valiente_logo.webp";
        } else if (usuario_id == 9) {
            img = "/emerson_vendedor_logo.webp";
        } else if (usuario_id == 10) {
            img = "/analuiza_logo.webp";
        }
        return img;
    };

    const {
        searchTerm,
        statusFilter,
        priorityFilter,
        createdByFilter,
        assignedToFilter,
        showMyTickets,
        showMlReclamacoes,
        startDate,
        endDate,
        currentPage,
        sortOption,
        activityFilter,
    } = filters;

    const {
        setSearchTerm,
        setStatusFilter,
        setPriorityFilter,
        setCreatedByFilter,
        setAssignedToFilter,
        setShowMyTickets,
        setShowMlReclamacoes,
        setStartDate,
        setEndDate,
        setCurrentPage,
        setSortOption,
        setActivityFilter,
    } = setFilters;

    const handleOpenModal = async (ticket) => {
        setSelectedTicket(ticket);
        setIsModalLoading(true);
        try {
            const response = await getTicket(ticket.id);
            setModalDetails(response.data.data);
            setModalStatus(response.data.data.status);
        } catch (error) {
            toast({ title: "Erro ao buscar detalhes do ticket.", status: "error", isClosable: true });
            handleCloseModal();
        } finally {
            setIsModalLoading(false);
        }
    };

    const handleCloseModal = () => {
        setSelectedTicket(null);
        setModalDetails(null);
        setModalStatus('');
    };

    const handleUpdateStatus = async () => {
        if (!selectedTicket || !modalStatus || !modalDetails) return;

        setIsUpdatingStatus(true);
        try {
            const formData = new FormData();
            formData.append('_method', 'PUT');
            formData.append('status', modalStatus);

            if (modalDetails.category) {
                formData.append('categoryIds[]', modalDetails.category.id);
            } else if (modalDetails.categories && modalDetails.categories.length > 0) {
                modalDetails.categories.forEach(c => formData.append('categoryIds[]', c.id));
            }

            await updateTicket(selectedTicket.id, formData);

            toast({ title: "Status atualizado com sucesso!", status: "success", isClosable: true });
            handleCloseModal();
            if (onUpdateSuccess) {
                onUpdateSuccess();
            }
        } catch (error) {
            toast({ title: "Erro ao atualizar status.", description: error.response?.data?.message || "Ocorreu um erro.", status: "error", isClosable: true });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const aplicarAtividade = (dias) => {
        setActivityFilter(activityFilter === dias ? '' : dias);
        setCurrentPage(1);
    };

    const totalPages = paginationData?.last_page || 1;

    return (
        <Box mt={4} mb={hideTable ? 2 : 12}>
            <Stack spacing={4} mb={hideTable ? 2 : 6} bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                <Flex justify="space-between" align="center" wrap="wrap">
                    <Button as={RouterLink} to="/tickets/novo" colorScheme="blue" size="sm" mb={{ base: 2, md: 0 }}>
                        Criar Novo Ticket
                    </Button>
                    <HStack spacing={4} w={{ base: '100%', md: 'auto' }}>
                        <FormControl display="flex" alignItems="center">
                            <FormLabel htmlFor="ml-reclamacoes" mb="0" whiteSpace="nowrap" fontSize="sm">Reclamações ML</FormLabel>
                            <Switch id="ml-reclamacoes" isChecked={showMlReclamacoes} onChange={(e) => setShowMlReclamacoes(e.target.checked)} colorScheme="yellow" />
                        </FormControl>
                        <FormControl display="flex" alignItems="center">
                            <FormLabel htmlFor="my-tickets" mb="0" whiteSpace="nowrap" fontSize="sm">Meus Tickets</FormLabel>
                            <Switch id="my-tickets" isChecked={showMyTickets} onChange={(e) => setShowMyTickets(e.target.checked)} />
                        </FormControl>
                    </HStack>
                </Flex>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={4} align="center">
                    <InputGroup flex={2}>
                        <InputLeftElement pointerEvents="none" children={<Icon as={FaSearch} color="gray.300" />} />
                        <Input placeholder="Buscar por ID, assunto ou autor..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </InputGroup>
                    {!hideTable && (
                    <Select placeholder="Filtrar por Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} flex={1}>
                        <option value="">Todos</option>
                        <option value="aberto">Aberto</option>
                        <option value="em_andamento">Em Andamento</option>
                        <option value="bloqueado">Bloqueado</option>
                        <option value="fechado">Concluido</option>
                    </Select>
                    )}
                    <Select placeholder="Filtrar por Prioridade" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} flex={1}>
                        <option value="">Todas</option>
                        <option value="baixa">Baixa</option>
                        <option value="media">Média</option>
                        <option value="alta">Alta</option>
                        <option value="urgente">Urgente</option>
                    </Select>
                </Stack>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={4} align="center">
                    <Select placeholder="Filtrar por 'Criado Por'" value={createdByFilter} onChange={(e) => setCreatedByFilter(e.target.value)} flex={1}>
                        <option value="">Todos</option>
                        {users.map(user => (
                            <option key={user.id} value={user.id}>{user.name}</option>
                        ))}
                    </Select>
                    <Select placeholder="Filtrar por 'Atribuído Para'" value={assignedToFilter} onChange={(e) => setAssignedToFilter(e.target.value)} flex={1}>
                        <option value="">Todos</option>
                        {users.map(user => (
                            <option key={user.id} value={user.id}>{user.name}</option>
                        ))}
                    </Select>
                </Stack>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={4} align="flex-end">
                    <FormControl>
                        <FormLabel htmlFor="start-date" fontSize="sm" mb={1}>Data Inicio</FormLabel>
                        <Input id="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} size="sm" />
                    </FormControl>
                    <FormControl>
                        <FormLabel htmlFor="end-date" fontSize="sm" mb={1}>Data Fim</FormLabel>
                        <Input id="end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} size="sm" />
                    </FormControl>
                </Stack>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={4} align="flex-end">
                    <FormControl maxW={{ base: '100%', md: '280px' }}>
                        <FormLabel fontSize="sm" mb={1}>Ordenar por</FormLabel>
                        <Select size="sm" value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
                            <option value="default">Padrão</option>
                            <option value="updated_desc">Atualizado recentemente</option>
                            <option value="updated_asc">Atualizado mais antigo</option>
                            <option value="created_desc">Criado recentemente</option>
                            <option value="created_asc">Criado mais antigo</option>
                            <option value="priority_desc">Maior prioridade</option>
                            <option value="priority_asc">Menor prioridade</option>
                            <option value="status">Por status</option>
                            <option value="title_asc">Titulo A-Z</option>
                            <option value="title_desc">Titulo Z-A</option>
                        </Select>
                    </FormControl>
                    <Button
                        variant="outline"
                        colorScheme="red"
                        size="sm"
                        onClick={onClearFilters}
                        leftIcon={<FaTimesCircle />}
                        alignSelf={{ base: 'stretch', md: 'flex-end' }}
                    >Limpar Filtros
                    </Button>
                    <HStack spacing={2} align="center" wrap="wrap" alignSelf={{ base: 'stretch', md: 'flex-end' }}>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium" whiteSpace="nowrap">
                            Movimentados nos últimos
                        </Text>
                        <ButtonGroup size="sm" isAttached variant="outline" w={{ base: '100%', md: 'auto' }}>
                            {JANELAS_ATIVIDADE.map((dias) => (
                                <Button
                                    key={dias}
                                    onClick={() => aplicarAtividade(dias)}
                                    bg={activityFilter === dias ? 'blue.500' : 'white'}
                                    color={activityFilter === dias ? 'white' : 'gray.600'}
                                    _hover={{ bg: activityFilter === dias ? 'blue.600' : 'gray.100' }}
                                    borderColor="gray.300"
                                    fontWeight="medium"
                                    flex={{ base: 1, md: 'none' }}
                                >
                                    {dias} dias
                                </Button>
                            ))}
                        </ButtonGroup>
                    </HStack>
                </Stack>
            </Stack>

            {viewToggle}

            {!hideTable && <Box bg="white" borderRadius="md" shadow="sm" borderWidth="1px" overflowX="auto">
                <Table variant="simple">
                    <Thead bg="gray.50">
                        <Tr>
                            <Th>ID</Th>
                            <Th>Assunto</Th>
                            <Th>Status</Th>
                            <Th>Prioridade</Th>
                            <Th>Categoria</Th>
                            <Th>Atribuído a</Th>
                            <Th>Criado</Th>
                            <Th>Ações</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {loading ? (
                            <Tr>
                                <Td colSpan={8} h="200px" textAlign="center">
                                    <Spinner size="lg" />
                                </Td>
                            </Tr>
                        ) : tickets?.length > 0 ? (
                            tickets.map((ticket) => {
                                const canEdit = currentUser && ticket.author && currentUser.id === ticket.author.id;
                                return (
                                    <Tr key={ticket.id} _hover={{ bg: 'gray.50' }}>
                                        <Td fontWeight="bold" color="gray.600">#{ticket.id}</Td>
                                        <Td maxW="350px">
                                            <Text fontWeight="bold" noOfLines={1}>{ticket.subject}</Text>
                                            <HStack spacing={2} mt={1.5}>
                                                <Avatar size="xs" name={ticket.author?.name} src={getImgUser(ticket.author?.id)} />
                                                <Text fontSize="sm" color="gray.500">por {ticket.author?.name}</Text>
                                            </HStack>
                                        </Td>
                                        <Td><Badge colorScheme={statusColors[ticket.status]} textTransform="capitalize">{statusLabels[ticket.status] || ticket.status}</Badge></Td>
                                        <Td><Badge colorScheme={priorityColors[ticket.priority]} textTransform="capitalize">{ticket.priority}</Badge></Td>
                                        <Td><Text noOfLines={1}>{ticket.categories?.map(c => c.name).join(', ')}</Text></Td>
                                        <Td><AvatarGroup size="xs" max={2}>{ticket.assignees?.map(user => (<Tooltip key={user.id} label={user.name} placement="top"><Avatar name={user.name} src={getImgUser(user.id)} /></Tooltip>))}</AvatarGroup>{ticket.assignees?.length === 0 && <Text color="gray.400">-</Text>}</Td>
                                        <Td><Text fontSize="sm" color="gray.600">{formatDistance(ticket.created_at)}</Text></Td>
                                        <Td>
                                            <HStack spacing={0}>
                                                <Tooltip label="Visualização rápida"><IconButton variant="ghost" colorScheme="teal" aria-label="Ver" icon={<FaEye />} size="sm" onClick={() => handleOpenModal(ticket)} /></Tooltip>
                                                {canEdit && (
                                                    <Tooltip label="Editar Ticket">
                                                        <IconButton as={RouterLink} to={`/tickets/${ticket.id}/editar`} variant="ghost" colorScheme="yellow" aria-label="Editar" icon={<FaPencilAlt />} size="sm" />
                                                    </Tooltip>
                                                )}
                                                <Tooltip label="Ver Detalhes"><IconButton as={RouterLink} to={`/tickets/${ticket.id}`} variant="ghost" colorScheme="blue" aria-label="Detalhes" icon={<FaExternalLinkAlt />} size="sm" /></Tooltip>
                                            </HStack>
                                        </Td>
                                    </Tr>
                                )
                            })
                        ) : (
                            <Tr>
                                <Td colSpan={8} h="200px" textAlign="center">
                                    <Text color="gray.500">Nenhum ticket encontrado com os filtros atuais.</Text>
                                </Td>
                            </Tr>
                        )}
                    </Tbody>
                </Table>
            </Box>}

            {!hideTable && totalPages > 1 && (
                <Flex justify="space-between" align="center" mt={4}>
                    <Text fontSize="sm" color="gray.600">Página {currentPage} de {totalPages}</Text>
                    <HStack>
                        <Button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} isDisabled={currentPage === 1} size="sm">Anterior</Button>
                        <Button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages} size="sm">Próxima</Button>
                    </HStack>
                </Flex>
            )}

            <Modal isOpen={!!selectedTicket} onClose={handleCloseModal} size="xl" isCentered scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    {isModalLoading ? (
                        <Flex justify="center" align="center" h="300px"><Spinner size="xl" /></Flex>
                    ) : modalDetails && (
                        <>
                            <ModalHeader fontSize="md" pr={12}>
                                <Flex align="center" gap={2} flexWrap="wrap">
                                    <Text>Ticket #{modalDetails.id}: {modalDetails.subject}</Text>
                                    <Tooltip label="Abrir pagina do ticket" hasArrow>
                                        <IconButton
                                            as={RouterLink}
                                            to={`/tickets/${modalDetails.id}`}
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="blue"
                                            icon={<FaExternalLinkAlt />}
                                            aria-label="Abrir ticket"
                                        />
                                    </Tooltip>
                                </Flex>
                            </ModalHeader>
                            <ModalCloseButton />

                            <ModalBody overflowY="auto" css={{ '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-track': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: 'gray', borderRadius: '24px' } }}>
                                <VStack spacing={4} align="start">
                                    {modalDetails.description
                                        ? <Box w="100%"><WikiContentViewer content={modalDetails.description} /></Box>
                                        : <Text color="gray.700" w="100%">Nenhuma descrição fornecida.</Text>}

                                    <Divider />
                                    <FormControl>
                                        <FormLabel fontSize="sm" fontWeight="bold">Alterar Status</FormLabel>
                                        <Select value={modalStatus} onChange={(e) => setModalStatus(e.target.value)}>
                                            <option value="aberto">Aberto</option>
                                            <option value="em_andamento">Em Andamento</option>
                                            <option value="bloqueado">Bloqueado</option>
                                            <option value="fechado">Concluido</option>
                                        </Select>
                                    </FormControl>
                                    <Divider />
                                    <Box w="full">
                                        <Text fontSize="sm" fontWeight="bold" color="gray.500">Atribuído a:</Text>
                                        {modalDetails.assignees?.length > 0 ? (<HStack mt={1}>{modalDetails.assignees.map(user => (<Tag key={user.id} size="md" colorScheme="gray" borderRadius="full"><Avatar src={getImgUser(user.id)} size="xs" name={user.name} ml={-1} mr={2} />{user.name}</Tag>))}</HStack>) : <Text>Ninguém atribuído</Text>}
                                    </Box>
                                    {modalDetails.attachments?.length > 0 && (
                                        <Box w="full">
                                            <Text fontSize="sm" fontWeight="bold" color="gray.500">Anexos:</Text>
                                            <VStack align="start" mt={1} w="100%">
                                                {modalDetails.attachments.map(file => (
                                                    <Button
                                                        key={file.id}
                                                        size="sm"
                                                        variant="link"
                                                        colorScheme="blue"
                                                        leftIcon={<FaDownload />}
                                                        onClick={() => handleDownloadAttachment(file.id, file.name)}
                                                        isLoading={downloadingId === file.id}
                                                        isDisabled={downloadingId !== null}
                                                        loadingText="Baixando..."
                                                        w="100%"
                                                        justifyContent="flex-start"
                                                        style={{ whiteSpace: 'normal', textAlign: 'left', wordBreak: 'break-all' }}
                                                    >
                                                        <Text noOfLines={1}>
                                                            {file.name}
                                                        </Text>
                                                    </Button>
                                                ))}
                                            </VStack>
                                        </Box>
                                    )}
                                </VStack>
                            </ModalBody>
                            <ModalFooter
                                flexDirection={{ base: 'column-reverse', sm: 'row' }}
                                justifyContent="space-between"
                                alignItems="stretch"
                                gap={2}
                            >
                                <Button
                                    variant="outline"
                                    colorScheme="orange"
                                    size="sm"
                                    leftIcon={<FaBell />}
                                    onClick={() => setIsNotifyOpen(true)}
                                    borderRadius="lg"
                                    w={{ base: 'full', sm: 'auto' }}
                                >
                                    Notificar
                                </Button>
                                <HStack w={{ base: 'full', sm: 'auto' }} spacing={2}>
                                    <Button variant="ghost" onClick={handleCloseModal} isDisabled={isUpdatingStatus} flex={{ base: 1, sm: 'initial' }}>Cancelar</Button>
                                    <Button colorScheme="blue" onClick={handleUpdateStatus} isLoading={isUpdatingStatus} loadingText="Atualizando..." flex={{ base: 1, sm: 'initial' }}>Atualizar Status</Button>
                                </HStack>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {modalDetails && (
                <TicketNotifyDialog
                    isOpen={isNotifyOpen}
                    onClose={() => setIsNotifyOpen(false)}
                    ticket={modalDetails}
                />
            )}
        </Box>
    );
};

export default TicketListPage;